/**
 * TG-A — live per-transaction progress chart. Samples `effectivePowerW` /
 * `accumulatedEnergyWh` / `currentSocPct` for one active connector once a
 * second (paused while the tab is hidden, like the power chart), resets when
 * the connector's active transaction changes, and renders the series via the
 * shared `TxSeriesChart`. Live-only; persisted history is TG-B.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ConnectorId } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useInterval } from '@/hooks/useInterval';
import { appendTxSample, type TxSample } from '@/lib/txTelemetry';
import { TxSeriesChart } from './TxSeriesChart';

const TICK_MS = 1_000;

export function TransactionChart({ connectorId }: { connectorId: ConnectorId }): JSX.Element {
  const bufferRef = useRef<readonly TxSample[]>([]);
  const txIdRef = useRef<string | null>(null);
  const [tickValue, setTick] = useState(0);
  const [paused, setPaused] = useState(() =>
    typeof document !== 'undefined' ? document.hidden : false,
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = (): void => setPaused(document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const sample = useCallback((): void => {
    const c = useStationStore.getState().connectors.get(connectorId);
    if (!c || c.activeTransactionId === null) return;
    const txId = String(c.activeTransactionId);
    if (txId !== txIdRef.current) {
      txIdRef.current = txId;
      bufferRef.current = [];
    }
    bufferRef.current = appendTxSample(bufferRef.current, {
      t: Date.now(),
      powerW: c.effectivePowerW,
      socPct: c.currentSocPct,
      energyWh: c.accumulatedEnergyWh,
    });
    setTick((t) => t + 1);
  }, [connectorId]);

  // Seed an immediate first sample so the chart isn't blank for the first second.
  useEffect(() => {
    sample();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInterval(sample, paused ? null : TICK_MS);

  const samples = useMemo(() => {
    void tickValue; // invalidation signal — bufferRef is a ref, untracked by deps
    return bufferRef.current;
  }, [tickValue]);

  return (
    <TxSeriesChart
      samples={samples}
      note={paused ? 'paused (tab hidden)' : 'this session'}
      testId={`tx-chart-${connectorId}`}
    />
  );
}
